import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useState, type ReactNode, type MouseEvent } from 'react';
import { Popover } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useDmFolders } from '@/lib/useDmFolders';
import { presenceDotClass } from '@/lib/presence';
import { Tooltip } from '@/components/Tooltip';
import { useLongPress } from '@/lib/useLongPress';
import { PlusIcon, UsersIcon } from '@/components/icons';
import { emitAppEvent } from '@/lib/appEvents';
import type { FriendUser } from '@/types/api';

/** A titled group in the friends panel (incoming / outgoing / online …). */
export function FriendsSection(props: { title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted">
        {props.title}
      </div>
      <div className="flex flex-col gap-0.5">{props.children}</div>
    </section>
  );
}

/** Small round action button for a friend row (accept / decline / message). */
export function RowIconButton(props: {
  label: string;
  variant: 'accept' | 'danger';
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip label={props.label}>
      <button
        type="button"
        aria-label={props.label}
        disabled={props.disabled}
        onClick={(e) => {
          e.stopPropagation();
          props.onClick();
        }}
        className={
          'grid h-7 w-7 shrink-0 place-items-center rounded-lg transition-colors disabled:opacity-50 ' +
          (props.variant === 'danger'
            ? 'text-danger hover:bg-danger/15'
            : 'text-success hover:bg-success/15')
        }
      >
        {props.children}
      </button>
    </Tooltip>
  );
}

/** A friend / request row: avatar + presence dot, name, @handle, optional
 *  action buttons on the right. The name area opens (or creates) the 1:1 DM
 *  when `onOpen` is set (not for outgoing requests, which have no chat yet). */
export function FriendRow(props: {
  friend: { user: FriendUser };
  onOpen?: () => void;
  actions?: ReactNode;
}) {
  const { user } = props.friend;
  const label = user.displayName || user.username;
  return (
    <div className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors elev-hover">
      <button
        type="button"
        onClick={props.onOpen}
        disabled={!props.onOpen}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-default"
      >
        <span className="relative shrink-0">
          <IdentityAvatar id={user.id} name={label} src={user.avatarUrl} size="sm" />
          <span
            className={
              'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface ' +
              presenceDotClass(user.status)
            }
            aria-hidden
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-foreground/90">{label}</span>
          <span className="truncate text-xs text-muted">@{user.username}</span>
        </span>
      </button>
      {props.actions && <div className="flex shrink-0 items-center gap-1">{props.actions}</div>}
    </div>
  );
}

/** A folder tab chip — horizontal pill (top placement) or stacked tile (left
 *  placement), with an unread-chats count badge. */
export function FolderChip(props: {
  label: string;
  active: boolean;
  count: number;
  vertical?: boolean;
  /** Render the count as a red attention badge (e.g. friend requests). */
  danger?: boolean;
  onClick: () => void;
  onContextMenu?: (e: MouseEvent) => void;
  /** Touch long-press opener — opens the same menu as `onContextMenu` at the
   *  press coordinates. Optional so non-folder chips can omit it. */
  onContextMenuAt?: (x: number, y: number) => void;
}) {
  // Touch long-press → same folder context menu as desktop right-click.
  const longPress = useLongPress((x, y) => props.onContextMenuAt?.(x, y));
  const badge =
    props.count > 0 ? (
      <span
        className={
          'inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums ' +
          (props.danger
            ? 'bg-danger text-white'
            : props.active
              ? 'bg-primary text-white'
              : 'bg-default text-muted')
        }
      >
        {props.count > 99 ? '99+' : props.count}
      </span>
    ) : null;

  if (props.vertical) {
    return (
      <button
        type="button"
        onClick={props.onClick}
        onContextMenu={props.onContextMenu}
        {...longPress}
        aria-pressed={props.active}
        className={
          'studio-dm-tab flex w-full select-none flex-col items-center gap-1 px-1 py-2 text-center transition-colors [-webkit-touch-callout:none] ' +
          (props.active ? 'text-foreground' : 'text-muted')
        }
      >
        <span className="w-full truncate text-[11px] font-medium leading-tight">{props.label}</span>
        {badge}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
      {...longPress}
      aria-pressed={props.active}
      className={
        'studio-dm-tab flex min-h-11 max-w-[140px] select-none items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors [-webkit-touch-callout:none] ' +
        (props.active
          ? 'text-foreground'
          : 'text-muted hover:text-foreground/90')
      }
    >
      <span className="truncate">{props.label}</span>
      {badge}
    </button>
  );
}

/** "＋" popover: start a group chat, or create a new (empty) folder by name. */
export function CreateFolderButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { createFolder, isMutating } = useDmFolders();

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createFolder(trimmed);
    } finally {
      setName('');
      setOpen(false);
    }
  }

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-label={t('dm.createMenu')}
        className="grid size-11 place-items-center rounded-full text-muted transition-colors elev-hover hover:text-foreground/90"
      >
        <PlusIcon className="h-4 w-4" />
      </Popover.Trigger>
      <Popover.Content className="w-64" placement="bottom start">
        <Popover.Dialog className="p-2">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setName('');
              emitAppEvent('groupdm:create');
            }}
            className="mb-2 flex min-h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-sm text-ink transition-colors hover:bg-default/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <UsersIcon className="size-4 shrink-0 text-muted" />
            {t('dm.newGroupChat')}
          </button>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              {t('dm.newFolder')}
            </label>
            <input
              autoFocus
              value={name}
              maxLength={40}
              disabled={isMutating}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setOpen(false);
                  setName('');
                }
              }}
              placeholder={t('dm.folderPlaceholder')}
              className="w-full rounded-lg border border-separator bg-default/40 px-2.5 py-1.5 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent"
            />
          </form>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
